<template>
  <div class="flex-1 flex flex-col overflow-hidden">
    <!-- Empty state -->
    <div v-if="!store.generating.value && !store.generationDone.value && !store.streamingText.value" class="flex-1 flex items-center justify-center text-gray-600 text-sm italic">
      Select a project and describe your content plan to get started
    </div>

    <!-- Streaming text (during generation) -->
    <div v-else-if="store.generating.value || (!store.generationDone.value && store.streamingText.value)" class="flex-1 overflow-y-auto p-6">
      <div class="max-w-2xl mx-auto">
        <div class="flex items-center gap-2 mb-4">
          <div class="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
          <span class="text-sm text-gray-600">Generating plan...</span>
        </div>
        <pre class="text-sm text-gray-900 whitespace-pre-wrap font-sans leading-relaxed">{{ store.streamingText.value }}</pre>
      </div>
    </div>

    <!-- Parsed entries (after generation) -->
    <template v-else-if="store.generationDone.value">
      <!-- Saved success banner -->
      <div v-if="store.savedPublication.value" class="shrink-0 bg-green-50 border-b border-green-200 px-6 py-3">
        <p class="text-sm text-green-800">
          Saved as publication "{{ store.savedPublication.value.name }}" with {{ store.savedPublication.value.entries_count }} entries.
        </p>
      </div>

      <div class="flex-1 overflow-y-auto p-6">
        <div class="max-w-2xl mx-auto space-y-3">
          <!-- Error -->
          <div v-if="store.generationError.value" class="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
            {{ store.generationError.value }}
          </div>

          <p class="text-sm text-gray-600 mb-2">{{ store.parsedEntries.value.length }} ideas generated. Edit titles and descriptions before saving.</p>

          <div
            v-for="(entry, index) in store.parsedEntries.value"
            :key="index"
            class="bg-white border border-gray-200 rounded-lg p-4 space-y-2"
          >
            <div class="flex items-start gap-2">
              <span class="text-xs font-medium text-gray-400 mt-1.5 shrink-0">{{ index + 1 }}.</span>
              <div class="flex-1 space-y-2">
                <input
                  :value="entry.title"
                  @input="store.updateEntryTitle(index, $event.target.value)"
                  class="w-full text-sm font-medium text-gray-900 border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-teal-400"
                />
                <textarea
                  :value="entry.description"
                  @input="store.updateEntryDescription(index, $event.target.value)"
                  rows="2"
                  class="w-full text-sm text-gray-700 border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-teal-400 resize-none"
                ></textarea>
              </div>
              <button
                @click="store.removeEntry(index)"
                class="text-gray-400 hover:text-red-500 transition-colors shrink-0 mt-1"
                title="Remove"
              >&times;</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Save bar -->
      <div v-if="store.parsedEntries.value.length && !store.savedPublication.value" class="shrink-0 border-t border-gray-200 px-6 py-3 bg-white flex items-center gap-3">
        <input
          v-model="publicationName"
          type="text"
          placeholder="Publication name..."
          class="flex-1 text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500"
        />
        <button
          @click="handleSave"
          :disabled="store.saving.value || !publicationName.trim()"
          class="px-4 py-2 rounded text-sm font-medium text-white transition-colors"
          :class="publicationName.trim() && !store.saving.value ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-300 cursor-not-allowed'"
        >
          <span v-if="store.saving.value">Saving...</span>
          <span v-else>Save as Publication</span>
        </button>
      </div>

      <!-- Save error -->
      <div v-if="store.saveError.value" class="shrink-0 bg-red-50 border-t border-red-200 px-6 py-2">
        <p class="text-sm text-red-800">{{ store.saveError.value }}</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, inject } from 'vue'

const store = inject('planner')
const publicationName = ref('')

async function handleSave() {
  await store.savePlan(publicationName.value)
}
</script>
